/**
 * Notification Service with User Profile Prefetching
 * Handles notification fetching and automatically prefetches user profiles
 * to populate actor information (name, username, avatar)
 */

import { getSdk } from "@/composables/useSdk";
import type { Notification } from "@/types/notification";

// Re-export Notification type for backward compatibility
export type { Notification } from "@/types/notification";

interface UserProfile {
  ulid: string;
  name: string;
  username: string;
  avatar?: string;
  verified?: boolean;
}

interface NotificationResource {
  notificationId: string;
  createdAt: string;
  fromUserId?: string | null;
  fromUserName?: string | null;
  fromUserUsername?: string | null;
  postId?: string | null;
  postTitle?: string | null;
  postType?: string | null;
  parentId?: string | null;
  rootId?: string | null;
  rootType?: string | null;
  notificationType: string;
  read: boolean;
  seen?: boolean;
  message?: string | null;
  reactionName?: string | null;
  reactionEmoji?: string | null;
  contentNotAvailableReason?: string | null;
  content?: {
    group_id?: number;
    group_ulid?: string;
    group_name?: string;
    [key: string]: unknown;
  } | null;
}

class NotificationService {
  // User profile cache to avoid repeated API calls
  private userProfileCache = new Map<string, UserProfile>();

  // Cache expiration time (5 minutes)
  private readonly CACHE_TTL = 5 * 60 * 1000;
  private cacheTimestamps = new Map<string, number>();

  /**
   * Fetch notifications with cursor pagination
   * Automatically prefetches user profiles for notification actors
   */
  async fetchNotifications(
    options: {
      cursor?: string;
      limit?: number;
      type?: string;
      unreadOnly?: boolean;
    } = {}
  ): Promise<{
    notifications: Notification[];
    nextCursor: string | null;
  }> {
    const sdk = getSdk();

    try {
      // Use SDK's getNotifications method
      const response = await sdk.getNotifications({
        cursor: options.cursor,
        limit: options.limit,
        type: options.type,
        unreadOnly: options.unreadOnly,
      });

      console.log("[NotificationService] Raw SDK response:", response);

      // Handle different response formats
      let rawNotifications: NotificationResource[] = [];
      let nextCursor: string | null = null;

      if (Array.isArray(response)) {
        rawNotifications = response;
      } else if (response && typeof response === "object") {
        if ("data" in response && Array.isArray(response.data)) {
          rawNotifications = response.data;
        }
        if ("nextCursor" in response) {
          nextCursor = response.nextCursor || null;
        }
      }

      console.log("[NotificationService] Parsed notifications count:", rawNotifications.length);
      if (rawNotifications.length > 0) {
        console.log("[NotificationService] First notification sample:", rawNotifications[0]);
      }

      // Fall back to using last notification's ID as cursor if not provided
      if (!nextCursor && rawNotifications.length > 0) {
        const lastNotification = rawNotifications[rawNotifications.length - 1];
        nextCursor = lastNotification?.notificationId || null;
      }

      // Map to UI Notification type
      // Note: Backend already provides fromUserName, fromUserUsername, message, etc.
      const notifications = rawNotifications.map((n) => this.mapNotification(n));

      console.log("[NotificationService] Mapped notifications count:", notifications.length);
      if (notifications.length > 0) {
        console.log("[NotificationService] First mapped notification:", notifications[0]);
      }

      return { notifications, nextCursor };
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      throw new Error("Failed to fetch notifications");
    }
  }

  /**
   * Prefetch user profiles for notifications
   * This populates the cache with user data before mapping notifications
   */
  private async prefetchUserProfiles(notifications: NotificationResource[]): Promise<void> {
    // Extract unique fromUserId values that need fetching
    const userIds = new Set<string>();

    for (const notification of notifications) {
      if (notification.fromUserId) {
        // Check if cached and not expired
        const cacheTime = this.cacheTimestamps.get(notification.fromUserId);
        const isExpired = !cacheTime || Date.now() - cacheTime > this.CACHE_TTL;

        if (!this.userProfileCache.has(notification.fromUserId) || isExpired) {
          userIds.add(notification.fromUserId);
        }
      }
    }

    if (userIds.size === 0) {
      return; // All needed profiles are already cached
    }

    // Fetch profiles in batch using single API call
    const sdk = getSdk();
    const userIdsArray = Array.from(userIds);

    try {
      // Use batched profile fetching to get all profiles in one API call
      const profiles = await sdk.getBatchedUserProfiles(userIdsArray);

      // Cache the fetched profiles
      profiles.forEach((profile: any) => {
        this.userProfileCache.set(profile.ulid, {
          ulid: profile.ulid,
          name: profile.name,
          username: profile.username,
          avatar: profile.avatar,
          verified: profile.verified,
        });
        this.cacheTimestamps.set(profile.ulid, Date.now());
      });
    } catch (error) {
      console.error("Failed to prefetch user profiles:", error);
      // Continue even if prefetch fails - notifications will show without actor names
    }
  }

  /**
   * Get cached user profile
   */
  private getUserProfile(userId: string): UserProfile | null {
    return this.userProfileCache.get(userId) || null;
  }

  /**
   * Map API NotificationResource to UI Notification type
   * Uses data directly from backend response
   */
  private mapNotification(raw: NotificationResource): Notification {
    const fromUserId = raw.fromUserId;
    const postId = raw.postId || raw.rootId;

    // Check if backend provided a meaningful message or just a generic one
    const hasGenericMessage = !raw.message ||
      raw.message === "You have a new notification" ||
      raw.message.trim() === "";

    // Always generate a specific message if the backend's is generic
    const message = hasGenericMessage ? this.generateMessage(raw) : (raw.message || this.generateMessage(raw));

    return {
      id: raw.notificationId,
      type: this.mapNotificationType(raw.notificationType),
      title: this.getNotificationTitle(raw.notificationType),
      message,
      read: raw.read,
      createdAt: new Date(raw.createdAt),
      notificationId: raw.notificationId,
      notificationType: raw.notificationType,
      fromUserId: fromUserId ?? undefined,
      fromUserName: raw.fromUserName ?? undefined,
      fromUserUsername: raw.fromUserUsername ?? undefined,
      postId: postId ?? undefined,
      postTitle: raw.postTitle ?? undefined,
      postType: raw.postType ?? undefined,
      reactionName: raw.reactionName ?? undefined,
      reactionEmoji: raw.reactionEmoji ?? undefined,
      data: {
        fromUserId: fromUserId ?? undefined,
        actorName: raw.fromUserName ?? undefined,
        actorUsername: raw.fromUserUsername ?? undefined,
        postId: postId ?? undefined,
        postContent: raw.postTitle ?? undefined,
        postType: raw.postType ?? undefined,
        parentId: raw.parentId ?? undefined,
        rootId: raw.rootId ?? undefined,
        rootType: raw.rootType ?? undefined,
        reactionName: raw.reactionName ?? undefined,
        reactionEmoji: raw.reactionEmoji ?? undefined,
        contentNotAvailableReason: raw.contentNotAvailableReason ?? undefined,
        // Group invite fields
        group_id: raw.content?.group_id,
        group_ulid: raw.content?.group_ulid,
        group_name: raw.content?.group_name,
        // Moderation fields
        reason: raw.content?.reason as string | undefined,
        content_type: raw.content?.content_type as string | undefined,
        violation_type: raw.content?.violation_type as string | undefined,
        content_ulid: raw.content?.content_ulid as string | undefined,
        post_title: raw.content?.post_title as string | undefined,
        post_body: raw.content?.post_body as string | undefined,
      },
      // Pass through raw content for direct access
      content: raw.content ?? undefined,
    };
  }

  /**
   * Map backend notification type to UI type
   */
  private mapNotificationType(type: string): Notification["type"] {
    const typeMap: Record<string, Notification["type"]> = {
      post_liked: "like",
      post_upvoted: "like",
      post_reacted: "reaction",
      post_commented: "comment",
      comment_reply: "comment",
      user_followed: "follow",
      follow_request: "follow_request",
      follow_accepted: "follow_accepted",
      mention: "mention",
      user_mentioned: "mention",
      post_mentioned: "mention",
      post_shared: "share",
      post_reposted: "repost",
      repost_with_comment: "repost",
      post_created: "post",
      message_received: "message",
      post_rated: "rating",
      song_rated: "rating",
      song_upload_succeeded: "system",
      song_added: "system",
      wsom_contest_created: "system",
      group_invite: "group_invite",
      post_approved: "post_approved",
      post_rejected: "post_rejected",
      badge_earned: "badge",
      content_removed: "content_removed",
      account_warning: "account_warning",
      account_banned: "account_banned",
      system_notification: "system",
      streak_milestone_3_days: "system",
      streak_milestone_7_days: "system",
      streak_milestone_14_days: "system",
      streak_milestone_30_days: "system",
      streak_milestone_60_days: "system",
      streak_milestone_90_days: "system",
      streak_milestone_365_days: "system",
    };

    return typeMap[type] || "system";
  }

  /**
   * Get notification title based on type
   */
  private getNotificationTitle(notificationType: string): string {
    const mappedType = this.mapNotificationType(notificationType);

    const titles: Record<Notification["type"], string> = {
      like: "New Like",
      reaction: "New Reaction",
      repost: "Reposted",
      comment: "New Comment",
      follow: "New Follower",
      follow_request: "Follow Request",
      follow_accepted: "Follow Accepted",
      mention: "You were mentioned",
      share: "Post Shared",
      post: "New Post",
      message: "New Message",
      system: "System Notification",
      rating: "New Rating",
      song_upload_succeeded: "Upload Complete",
      group_invite: "Group Invite",
      post_approved: "Post Approved",
      post_rejected: "Post Rejected",
      badge: "New Badge",
      content_removed: "Content Removed",
      account_warning: "Account Warning",
      account_banned: "Account Suspended",
    };

    return titles[mappedType] || "New Notification";
  }

  /**
   * Generate notification message
   * Fallback when backend doesn't provide a message
   */
  private generateMessage(notification: NotificationResource): string {
    const type = notification.notificationType;
    const actorName = notification.fromUserName || "Someone";

    // Check if it's a streak milestone type
    if (type?.includes("streak_milestone")) {
      const days = type.match(/streak_milestone_(\d+)_days/)?.[1];
      if (days === "3") return "You're off to a great start. Let's keep this streak going!";
      if (days === "7") return "Amazing! You've been consistent for a whole week!";
      if (days === "14") return "You're on fire! Two weeks of dedication!";
      if (days === "30") return "Incredible achievement! A full month of consistency!";
      if (days === "60") return "Outstanding! Two months of amazing progress!";
      if (days === "90") return "Legendary! Three months of pure dedication!";
      if (days === "365") return "Unbelievable! A full year of consistency!";
      return "Congratulations on your streak milestone!";
    }

    // Generate messages with actor name
    switch (type) {
      case "post_liked":
      case "post_upvoted":
        return `${actorName} liked your post`;
      case "post_reacted":
        return notification.reactionName
          ? `${actorName} reacted ${notification.reactionName} to your post`
          : `${actorName} reacted to your post`;
      case "post_commented":
      case "comment_reply":
        return `${actorName} commented on your post`;
      case "user_followed":
        return `${actorName} followed you`;
      case "follow_request":
        return `${actorName} requested to follow you`;
      case "follow_accepted":
        return `${actorName} accepted your follow request`;
      case "user_mentioned":
      case "post_mentioned":
        return `${actorName} mentioned you`;
      case "mention": 
        return `${actorName} mentioned you`;
      case "post_reposted":
      case "repost_with_comment":
        return `${actorName} reposted your post`;
      case "post_shared":
        return `${actorName} shared your post`;
      case "post_rated":
      case "song_rated":
        return `${actorName} rated your post`;
      case "song_upload_succeeded":
        return notification.postTitle
          ? `"${notification.postTitle}" has been successfully uploaded!`
          : "Your song has been successfully uploaded!";
      case "group_invite":
        return notification.content?.group_name
          ? `You've been invited to group ${notification.content.group_name}`
          : `${actorName} invited you to join a group`;
      case "song_added":
        return `${actorName} added a new song`;
      case "post_created":
        // Check if it's a song post
        if (notification.postType === "SONG" || notification.rootType === "SONG") {
          return notification.postTitle
            ? `${actorName} added a new song: ${notification.postTitle}`
            : `${actorName} added a new song`;
        }
        return notification.postTitle
          ? `${actorName} posted: ${notification.postTitle}`
          : `${actorName} posted something new`;
      case "wsom_contest_created":
        return notification.content?.contest_name
          ? `A new WSOM contest is available: ${notification.content.contest_name}`
          : "A new WSOM contest is available";
      case "badge_earned":
        return notification.content?.badge_name
          ? `You earned the "${notification.content.badge_name}" badge!`
          : "You earned a new badge!";
      case "content_removed": {
        const contentType = notification.content?.content_type || "post";
        const reason = notification.content?.reason || "policy violation";
        const preview = notification.postTitle
          ? `"${notification.postTitle.substring(0, 50)}${notification.postTitle.length > 50 ? '...' : ''}"`
          : `Your ${contentType}`;
        return `${preview} has been removed for the following policy violation: ${reason}`;
      }
      case "account_warning": {
        const warningReason = notification.content?.reason || "policy violation";
        return `You have received a warning for: ${warningReason}`;
      }
      case "account_banned": {
        const banReason = notification.content?.reason || "policy violations";
        return `Your account has been suspended for: ${banReason}`;
      }
      default:
        return "You have a new notification";
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    const sdk = getSdk();

    try {
      await sdk.markNotificationRead(notificationId);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      throw new Error("Failed to mark notification as read");
    }
  }

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(notificationIds: string[]): Promise<void> {
    const sdk = getSdk();

    try {
      await sdk.markNotificationsRead(notificationIds);
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
      throw new Error("Failed to mark notifications as read");
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(lastNotificationId: string): Promise<void> {
    const sdk = getSdk();

    try {
      await sdk.markAllNotificationsRead(lastNotificationId);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      throw new Error("Failed to mark all notifications as read");
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    const sdk = getSdk();

    try {
      await sdk.deleteNotification(notificationId);
    } catch (error) {
      console.error("Failed to delete notification:", error);
      throw new Error("Failed to delete notification");
    }
  }

  /**
   * Delete multiple notifications
   */
  async deleteNotifications(notificationIds: string[]): Promise<void> {
    const sdk = getSdk();

    try {
      await sdk.deleteNotifications(notificationIds);
    } catch (error) {
      console.error("Failed to delete notifications:", error);
      throw new Error("Failed to delete notifications");
    }
  }

  /**
   * Get notification counts (includes unread count)
   */
  async getNotificationCounts(): Promise<{
    total_count: number;
    read_count: number;
    read_count_false: number;
    seen_count: number;
    seen_count_false: number;
  }> {
    const sdk = getSdk();

    try {
      return await sdk.getNotificationCounts();
    } catch (error) {
      console.error("Failed to get notification counts:", error);
      throw new Error("Failed to get notification counts");
    }
  }

  /**
   * Get unread count (convenience method)
   */
  async getUnreadCount(): Promise<number> {
    const sdk = getSdk();
    return await sdk.getUnreadNotificationCount();
  }

  /**
   * Clear user profile cache
   * Useful for forcing a refresh of user data
   */
  clearProfileCache(): void {
    this.userProfileCache.clear();
    this.cacheTimestamps.clear();
  }

  /**
   * Clear expired cache entries
   */
  private cleanupExpiredCache(): void {
    const now = Date.now();
    for (const [userId, timestamp] of this.cacheTimestamps.entries()) {
      if (now - timestamp > this.CACHE_TTL) {
        this.userProfileCache.delete(userId);
        this.cacheTimestamps.delete(userId);
      }
    }
  }

  // =============================================================
  // DEPRECATED METHODS - For backward compatibility only
  // =============================================================

  /**
   * @deprecated Use fetchNotifications instead
   */
  async getNotifications(
    options: {
      cursor?: string;
      limit?: number;
      type?: string;
    } = {}
  ): Promise<{
    notifications: Notification[];
    nextCursor: string | null;
  }> {
    console.warn(
      "[DEPRECATED] notificationService.getNotifications() is deprecated. Use fetchNotifications() or TanStack Query hooks instead."
    );
    return this.fetchNotifications(options);
  }
}

export default new NotificationService();
